import {
  expenseInvoiceFa3CorrectionVariants,
  expenseInvoiceFa3Dispositions,
  expenseInvoiceFa3Families,
  expenseInvoiceFa3LineRoles,
  expenseInvoiceFa3LineSources,
  expenseInvoiceFa3RelationTypes,
  expenseInvoiceFa3SupportStatuses,
  type ExpenseInvoiceDto,
  type ExpenseInvoiceFa3CorrectionVariant,
  type ExpenseInvoiceFa3Disposition,
  type ExpenseInvoiceFa3DocumentLinkDto,
  type ExpenseInvoiceFa3Family,
  type ExpenseInvoiceFa3LineRole,
  type ExpenseInvoiceFa3LineSource,
  type ExpenseInvoiceFa3SemanticDto,
  type ExpenseInvoiceFa3SummaryDto,
  type ExpenseInvoiceFa3SupportStatus,
  type ExpenseInvoiceItemDto,
  type ExpenseInvoiceListDto,
  type ExpenseInvoicePagedListDto,
  type ExpenseInvoiceTagDto,
} from '../Types/expenseInvoice.types';
import { normalizeExpenseInvoiceDraftBlockReason } from '../Utils/expenseInvoiceDraftBlockReason';
import {
  isSimplifiedExpenseInvoiceDocument,
  normalizeExpenseInvoiceDocumentKind,
} from '../Utils/expenseInvoiceDocumentKind';

const normalizeString = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeRequiredString = (value: string | null | undefined): string =>
  normalizeString(value) ?? '';

const isOneOf = <T extends readonly string[]>(
  value: string | null | undefined,
  allowed: T
): value is T[number] => typeof value === 'string' && allowed.includes(value as T[number]);

const normalizeFa3Family = (value: string | null | undefined): ExpenseInvoiceFa3Family | null =>
  isOneOf(value, expenseInvoiceFa3Families) ? value : null;

const normalizeFa3Disposition = (
  value: string | null | undefined
): ExpenseInvoiceFa3Disposition | null =>
  isOneOf(value, expenseInvoiceFa3Dispositions) ? value : null;

const normalizeFa3SupportStatus = (
  value: string | null | undefined
): ExpenseInvoiceFa3SupportStatus | null =>
  isOneOf(value, expenseInvoiceFa3SupportStatuses) ? value : null;

const normalizeFa3LineSource = (
  value: string | null | undefined
): ExpenseInvoiceFa3LineSource | null =>
  isOneOf(value, expenseInvoiceFa3LineSources) ? value : null;

const normalizeFa3CorrectionVariant = (
  value: string | null | undefined
): ExpenseInvoiceFa3CorrectionVariant | null =>
  isOneOf(value, expenseInvoiceFa3CorrectionVariants) ? value : null;

const normalizeFa3LineRole = (value: string | null | undefined): ExpenseInvoiceFa3LineRole | null =>
  isOneOf(value, expenseInvoiceFa3LineRoles) ? value : null;

const normalizeExpenseInvoiceTags = (
  tags: ExpenseInvoiceTagDto[] | null | undefined
): ExpenseInvoiceTagDto[] =>
  (tags ?? [])
    .map((tag) => {
      const normalizedName = normalizeString(tag?.name);

      return normalizedName
        ? {
            ...tag,
            name: normalizedName,
          }
        : null;
    })
    .filter(
      (tag): tag is ExpenseInvoiceTagDto =>
        typeof tag?.id === 'number' && Number.isFinite(tag.id) && typeof tag.name === 'string'
    );

const normalizeExpenseInvoiceFa3DocumentLink = (
  link: ExpenseInvoiceFa3DocumentLinkDto | null | undefined
): ExpenseInvoiceFa3DocumentLinkDto | null => {
  if (!link || !isOneOf(link.relationType, expenseInvoiceFa3RelationTypes)) {
    return null;
  }

  return {
    relationType: link.relationType,
    relatedExpenseInvoiceId:
      typeof link.relatedExpenseInvoiceId === 'number' ? link.relatedExpenseInvoiceId : null,
    relatedDocumentReference: normalizeString(link.relatedDocumentReference),
    externalInvoiceNumber: normalizeString(link.externalInvoiceNumber),
    externalKsefNumber: normalizeString(link.externalKsefNumber),
    externalIssueDate: normalizeString(link.externalIssueDate),
    isResolved: Boolean(link.isResolved),
  };
};

const normalizeExpenseInvoiceFa3Summary = (
  summary: ExpenseInvoiceFa3SummaryDto | null | undefined
): ExpenseInvoiceFa3SummaryDto | null => {
  if (!summary) {
    return null;
  }

  const family = normalizeFa3Family(summary.family);
  const disposition = normalizeFa3Disposition(summary.disposition);
  const supportStatus = normalizeFa3SupportStatus(summary.supportStatus);

  if (!family || !disposition || !supportStatus) {
    return null;
  }

  return {
    family,
    disposition,
    supportStatus,
    correctionVariant: normalizeFa3CorrectionVariant(summary.correctionVariant),
    unsupportedReason: normalizeString(summary.unsupportedReason),
    hasUnresolvedLinks: Boolean(summary.hasUnresolvedLinks),
  };
};

const normalizeExpenseInvoiceFa3Semantic = (
  semantic: ExpenseInvoiceFa3SemanticDto | null | undefined
): ExpenseInvoiceFa3SemanticDto | null => {
  if (!semantic) {
    return null;
  }

  const family = normalizeFa3Family(semantic.family);
  const supportStatus = normalizeFa3SupportStatus(semantic.supportStatus);
  const disposition = normalizeFa3Disposition(semantic.disposition);
  const lineSource = normalizeFa3LineSource(semantic.lineSource);

  if (!family || !supportStatus || !disposition || !lineSource) {
    return null;
  }

  return {
    family,
    supportStatus,
    sourceDocumentKind: normalizeRequiredString(semantic.sourceDocumentKind),
    disposition,
    lineSource,
    correctionVariant: normalizeFa3CorrectionVariant(semantic.correctionVariant),
    correctionReason: normalizeString(semantic.correctionReason),
    sourceKsefNumber: normalizeString(semantic.sourceKsefNumber),
    unsupportedReason: normalizeString(semantic.unsupportedReason),
    links: (semantic.links ?? [])
      .map(normalizeExpenseInvoiceFa3DocumentLink)
      .filter((link): link is ExpenseInvoiceFa3DocumentLinkDto => link !== null),
  };
};

const normalizeExpenseInvoiceItem = (item: ExpenseInvoiceItemDto): ExpenseInvoiceItemDto => {
  const lineRole = normalizeFa3LineRole(item.fa3Semantic?.lineRole);

  return {
    ...item,
    name: normalizeRequiredString(item.name),
    vatExemptNote: normalizeString(item.vatExemptNote),
    fa3Semantic:
      item.fa3Semantic && lineRole
        ? {
            lineRole,
            sourceSection: normalizeRequiredString(item.fa3Semantic.sourceSection),
            sourceLineNumber:
              typeof item.fa3Semantic.sourceLineNumber === 'number'
                ? item.fa3Semantic.sourceLineNumber
                : null,
          }
        : null,
  };
};

const resolveDocumentReference = (invoice: {
  id: number;
  documentKind: ExpenseInvoiceDto['documentKind'];
  documentReference?: string | null;
  invoiceNumber?: string | null;
}): string => {
  const explicitReference = normalizeString(invoice.documentReference);

  if (explicitReference) {
    return explicitReference;
  }

  const invoiceNumber = normalizeString(invoice.invoiceNumber);
  if (invoiceNumber) {
    return invoiceNumber;
  }

  return isSimplifiedExpenseInvoiceDocument(invoice.documentKind)
    ? `SC-${invoice.id}`
    : `DOC-${invoice.id}`;
};

const resolveDisplayIssuerName = (invoice: {
  documentKind: ExpenseInvoiceDto['documentKind'];
  displayIssuerName?: string | null;
  supplierName?: string | null;
}): string => {
  const explicitDisplayIssuerName = normalizeString(invoice.displayIssuerName);

  if (explicitDisplayIssuerName) {
    return explicitDisplayIssuerName;
  }

  const supplierName = normalizeString(invoice.supplierName);
  if (supplierName) {
    return supplierName;
  }

  return isSimplifiedExpenseInvoiceDocument(invoice.documentKind)
    ? 'Koszt uproszczony'
    : 'Brak dostawcy';
};

export const normalizeExpenseInvoiceListItem = (
  invoice: ExpenseInvoiceListDto
): ExpenseInvoiceListDto => {
  const documentKind = normalizeExpenseInvoiceDocumentKind(invoice.documentKind);

  return {
    ...invoice,
    documentKind,
    documentReference: resolveDocumentReference({ ...invoice, documentKind }),
    displayIssuerName: resolveDisplayIssuerName({ ...invoice, documentKind }),
    invoiceNumber: normalizeString(invoice.invoiceNumber),
    supplierName: normalizeString(invoice.supplierName),
    draftBlockReason: normalizeExpenseInvoiceDraftBlockReason(invoice.draftBlockReason),
    description: normalizeString(invoice.description),
    tags: normalizeExpenseInvoiceTags(invoice.tags),
    fa3Summary: normalizeExpenseInvoiceFa3Summary(invoice.fa3Summary),
  };
};

export const normalizeExpenseInvoiceDetails = (invoice: ExpenseInvoiceDto): ExpenseInvoiceDto => {
  const documentKind = normalizeExpenseInvoiceDocumentKind(invoice.documentKind);

  return {
    ...invoice,
    documentKind,
    documentReference: resolveDocumentReference({ ...invoice, documentKind }),
    displayIssuerName: resolveDisplayIssuerName({ ...invoice, documentKind }),
    invoiceNumber: normalizeString(invoice.invoiceNumber),
    supplierName: normalizeString(invoice.supplierName),
    draftBlockReason: normalizeExpenseInvoiceDraftBlockReason(invoice.draftBlockReason),
    description: normalizeString(invoice.description),
    tags: normalizeExpenseInvoiceTags(invoice.tags),
    items: (invoice.items ?? []).map(normalizeExpenseInvoiceItem),
    saleDate: normalizeString(invoice.saleDate),
    paymentDueDate: normalizeString(invoice.paymentDueDate),
    supplierAddress: normalizeString(invoice.supplierAddress),
    supplierNip: normalizeString(invoice.supplierNip),
    supplierBankAccount: normalizeString(invoice.supplierBankAccount),
    paidDate: normalizeString(invoice.paidDate),
    paymentNote: normalizeString(invoice.paymentNote),
    notes: normalizeString(invoice.notes),
    fa3Semantic: normalizeExpenseInvoiceFa3Semantic(invoice.fa3Semantic),
  };
};

export const normalizeExpenseInvoicePagedList = (
  response: ExpenseInvoicePagedListDto
): ExpenseInvoicePagedListDto => ({
  ...response,
  items: (response.items ?? []).map(normalizeExpenseInvoiceListItem),
});
